import type { ReactNode } from "react";
import { Card, CardContent } from "@volca/ui/components/ui/card";
import { Stack } from "@volca/ui/components/ui/stack";
import { cn } from "@volca/ui/lib/utils";
import { judge, points } from "@volca/ui/trades";
import { useReportCurrency } from "@/profitability/currency";
import { MissingValue } from "@/profitability/missing-value";
import type { RecordObject, RecordRef } from "@/profitability/records/objects";
import { RecordChip } from "@/profitability/records/record-chip";
import { SectionHeading } from "@/profitability/records/section-heading";
import { money } from "@/profitability/review/labels";
import { isMoneyRate, useReviewSpec } from "@/profitability/review/review-spec";
import { formatChange } from "./overview-figures";
import type { ChangedRecord, DatasetChanges, ReportOverview } from "./types";

/** New or gone records are news only when few are: jobs turn over weekly. */
const TURNOVER = 0.3;
/** Cost posted after the fact is news from this much; a few dollars is noise. */
const LATE_COST_FLOOR = 100;

/** The record a change names, by its id: opaque, never split or rebuilt. */
const changedRef = (object: RecordObject, record: ChangedRecord) => ({
  dataset: object.dataset.id,
  recordId: record.record_id ?? record.key,
});

const Chips = ({
  object,
  records,
  onOpenRecord,
}: {
  object: RecordObject;
  records: ChangedRecord[];
  onOpenRecord: (ref: RecordRef) => void;
}) => (
  <span className="inline-flex flex-wrap items-center gap-1">
    {records.map((record) => (
      <RecordChip
        key={record.key}
        object={object}
        name={record.name}
        onOpen={() => onOpenRecord(changedRef(object, record))}
      />
    ))}
  </span>
);

/**
 * A sentence under a kind's moves, its records named inline: "Hugo Mancini is
 * new this week." Records past the sample are counted, not listed.
 */
const Said = ({
  object,
  records,
  total,
  onOpenRecord,
  children,
}: {
  object: RecordObject;
  records: ChangedRecord[];
  total: number;
  onOpenRecord: (ref: RecordRef) => void;
  children: ReactNode;
}) => (
  <li className="flex flex-wrap items-center gap-x-1 gap-y-1 text-subtle">
    <Chips object={object} records={records} onOpenRecord={onOpenRecord} />
    {total > records.length && <span>and {total - records.length} more</span>}
    <span>{children}</span>
  </li>
);

/**
 * What of a kind's changes is worth a line: records new or gone (when few
 * are), results that moved, and cost added after billing past the floor.
 */
const worthSaying = (changes: DatasetChanges, object: RecordObject) => {
  const rows = object.dataset.rows.length || 1;
  const news = (side?: { count: number }) =>
    !!side?.count && side.count / rows <= TURNOVER;
  const moved = (changes.changed ?? []).filter(
    (row) => row.result_change !== null && Math.abs(row.result_change) >= 1,
  );
  const late =
    !!changes.late_costs?.count &&
    Math.abs(changes.late_costs.cost_change) >= LATE_COST_FLOOR;
  return {
    news,
    moved,
    late,
    any:
      late ||
      news(changes.appeared) ||
      news(changes.disappeared) ||
      moved.length > 0,
  };
};

/** One dataset's changes, with labels for both numeric columns. */
const Lines = ({
  changes,
  object,
  noun,
  onOpenRecord,
}: {
  changes: DatasetChanges;
  object: RecordObject;
  /** The period's word: "week", "month". */
  noun: string;
  onOpenRecord: (ref: RecordRef) => void;
}) => {
  const currency = useReportCurrency();
  const spec = useReviewSpec();
  // A change in the rate reads in points only when the rate is a share of
  // money; hours over hours, or a figure the script calls `other`, does not.
  const rated = isMoneyRate(spec);
  const result = object.dataset.columns.find(
    (column) => column.role === "result",
  );
  const better = result?.better ?? spec.metric.better;
  const resultLabel = result?.label ?? result?.key ?? "Result";
  const metric = spec.metric.label;
  const { news, moved, late } = worthSaying(changes, object);
  if (
    !late &&
    !news(changes.appeared) &&
    !news(changes.disappeared) &&
    !moved.length
  )
    return null;
  return (
    <li className="@container/change-group flex min-w-0 flex-col gap-1.5">
      <Stack
        direction="row"
        align="end"
        justify="between"
        gap={2}
        className="text-xs text-subtle"
      >
        <span className="min-w-0">{object.plural}</span>
        <span
          className={cn(
            "hidden shrink-0 grid-cols-[5rem] gap-2 text-end @sm/change-group:grid",
            {
              "grid-cols-[5rem_4rem]": rated,
            },
          )}
        >
          <span>{resultLabel}</span>
          {rated && <span>{metric} (pts)</span>}
        </span>
      </Stack>
      <ul className="flex flex-col gap-2">
        {moved.slice(0, 3).map((row) => (
          <li
            key={row.key}
            className="grid min-w-0 grid-cols-1 items-start gap-2 @sm/change-group:grid-cols-[minmax(0,1fr)_auto] @sm/change-group:items-center"
          >
            <RecordChip
              object={object}
              name={row.name}
              onOpen={() => onOpenRecord(changedRef(object, row))}
            />
            {/* The figures in a column of their own, so a list reads down. */}
            <span
              className={cn(
                "grid grid-cols-[7rem] items-baseline gap-3 tabular-nums @sm/change-group:ms-auto @sm/change-group:grid-cols-[5rem] @sm/change-group:gap-2 @sm/change-group:text-end",
                {
                  "grid-cols-[7rem_5rem] @sm/change-group:grid-cols-[5rem_4rem]":
                    rated,
                },
              )}
            >
              {/* A result missing in either period has no change (FIN-004):
                  it reads as missing, never as +$0. */}
              {row.result_change === null ? (
                <MissingValue />
              ) : (
                <span
                  className={cn({
                    "text-success": judge(row.result_change, better) === "good",
                    "text-danger": judge(row.result_change, better) === "bad",
                  })}
                >
                  <span className="block text-xs text-subtle @sm/change-group:sr-only">
                    {resultLabel}
                    <span className="sr-only"> change: </span>
                  </span>
                  {formatChange(
                    {
                      change: row.result_change,
                      type: result?.type ?? "currency",
                    },
                    currency,
                  )}
                </span>
              )}
              {rated && (
                <span
                  title={
                    row.margin_change === null
                      ? `${metric} change unavailable`
                      : `${metric} change ${points(row.margin_change)}`
                  }
                  className="text-xs text-subtle"
                >
                  <span className="block text-xs text-subtle @sm/change-group:sr-only">
                    {metric} (pts)<span className="sr-only"> change: </span>
                  </span>
                  {row.margin_change === null ? (
                    <MissingValue />
                  ) : (
                    points(row.margin_change)
                  )}
                </span>
              )}
            </span>
          </li>
        ))}
        {/* What else happened to the kind, said in a sentence each. */}
        {news(changes.appeared) && (
          <Said
            object={object}
            records={changes.appeared!.sample}
            total={changes.appeared!.count}
            onOpenRecord={onOpenRecord}
          >
            {changes.appeared!.count === 1 ? "is" : "are"} new this {noun}.
          </Said>
        )}
        {news(changes.disappeared) && (
          <Said
            object={object}
            records={changes.disappeared!.sample}
            total={changes.disappeared!.count}
            onOpenRecord={onOpenRecord}
          >
            {changes.disappeared!.count === 1 ? "isn't" : "aren't"} in this{" "}
            {noun}.
          </Said>
        )}
        {late && changes.late_costs && (
          <Said
            object={object}
            records={changes.late_costs.rows}
            total={changes.late_costs.count}
            onOpenRecord={onOpenRecord}
          >
            cost{" "}
            <span className="text-ink tabular-nums">
              {money(Math.abs(changes.late_costs.cost_change), currency)}
            </span>{" "}
            more after billing.
          </Said>
        )}
      </ul>
    </li>
  );
};

/**
 * What changed among the records that stay from period to period: the units
 * and technicians that moved most, records new or gone (when few are), and
 * records from before that picked up more cost after the fact.
 */
export const OverviewChanges = ({
  changes,
  objects,
  since,
  noun,
  onOpenRecord,
}: {
  changes: ReportOverview["changes"];
  objects: RecordObject[];
  since: string;
  /** The period's word: "week", "month". */
  noun: string;
  onOpenRecord: (ref: RecordRef) => void;
}) => {
  if (!changes) return null;
  // An average has no records of its own to match; say so instead of
  // leaving the section out without a word.
  if (!changes.available)
    return changes.reason ? (
      <Card role="region" aria-label={`Since ${since}`}>
        <CardContent className="flex flex-col gap-2">
          <SectionHeading>Change since {since}</SectionHeading>
          <p className="text-sm text-subtle">{changes.reason}</p>
        </CardContent>
      </Card>
    ) : null;
  const shown = changes.datasets.flatMap((item) => {
    const object = objects.find(
      (candidate) => candidate.dataset.id === item.dataset,
    );
    return object && item.available !== false && worthSaying(item, object).any
      ? [{ item, object }]
      : [];
  });
  // Nothing worth a line: no heading over an empty list.
  if (!shown.length) return null;
  return (
    <Card
      role="region"
      aria-label={`Since ${since}`}
      className="@container/changes min-w-0"
    >
      <CardContent className="flex flex-col gap-4">
        <SectionHeading>Change since {since}</SectionHeading>
        <ul className="grid min-w-0 grid-cols-1 gap-x-10 gap-y-6 text-sm text-ink @4xl/changes:grid-cols-2 @7xl/changes:grid-cols-3 @7xl/changes:gap-x-12">
          {shown.map(({ item, object }) => (
            <Lines
              key={item.dataset}
              changes={item}
              object={object}
              noun={noun}
              onOpenRecord={onOpenRecord}
            />
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};
